// import '@/styles/grid.css';
import {Fragment, useEffect, useRef, useState} from 'react';
import {NavLink} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {Button, ButtonGroup, IconButton} from '@/components/Button';
import {Footer} from '@/components/Footer';
import {NavBar} from '@/components/NavBar';
import {Typography} from '@/components/Typography';
import {useBreakpoints, useSession} from '@/hooks';
import {useAirbridgeTracking, useGA4WithParams} from '@/hooks/tracking';
import {paths, SERVICE_GUIDE} from '@/router';
import {
  ColumnFlexBox,
  Divider,
  Flexbox,
  FlexCenter,
  FlexFullGrow,
  HorizontalFullContainer,
  PaddingBox,
  ScrollBody,
} from '@/styled-components';
import {palette} from '@/styles';
import AccPlusIcon from './acc-add-28.svg';
import AccMinusIcon from './acc-minus-28.svg';
import {ACCORDION_CONTENTS, AccordionContent} from './constants';
import PartnersLogo from './brand-logo-rolling.png';
import {ReactComponent as IconCheck} from './icon-check.svg';
import MainImg002 from './main002.png';
import MapBg from './map-bg.png';
import {MobileIntro} from './MobileIntro';
import OutLinkIcon from './open_in_new.png';
import OutLinkIconGray from './open_in_new_gray.png';

const Intro = () => {
  const {isMobile} = useBreakpoints();
  const tracking = useGA4WithParams();
  const trackAirbridge = useAirbridgeTracking();
  const isTracked = useRef<boolean>(false);
  const {isPending} = useSession();
  const onGuideClick = () => {
    trackAirbridge('click_preview_page');
    tracking('guide_click', {
      event_callback: () => window.open(SERVICE_GUIDE, '_blank'),
    });
  };

  useEffect(() => {
    if (isTracked.current || isPending) {
      return;
    }

    trackAirbridge('airbridge.ecommerce.home.viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  useEffect(() => {
    const container = document.querySelector(`.logo-slider`);
    const slide = document.querySelector(`.logo-slide`);

    if (container && slide) {
      const cloneSlide = slide.cloneNode(true);
      container.appendChild(cloneSlide);
    }
  }, [isMobile]);

  if (isMobile) {
    return <MobileIntro />;
  }

  return (
    <HorizontalFullContainer>
      <NavBar />
      <Body>
          <Content>
            <Header>
              <Headline>
                필요한 모든 상권 정보를 한 번에!
                <h1>마이프차 지도</h1>
              </Headline>
              <PaddingBox top={28}>
                <Typography
                  variant="medium20"
                  color={palette.gray600}
                  lineHeight={1.5}
                >
                  상권이 가장 중요한 프랜차이즈 창업 시장에서 검증된 상권 분석
                  전문 서비스
                </Typography>
              </PaddingBox>
              <PaddingBox top={60}>
                <FlexCenter>
                  <ButtonGroup spacing={4}>
                    <NavLink to={paths.pricing_root}>
                      <BigButton
                        size="large"
                        variant="fill"
                        color="secondary"
                        as={Button}
                      >
                        마이프차 지도 요금제 보기
                      </BigButton>
                    </NavLink>
                    <TealBigButton
                      size="large"
                      icon={OutLinkIcon}
                      iconSize={20}
                      gap="8px"
                      reverse
                      as={IconButton}
                      onClick={onGuideClick}
                    >
                      미리보기
                    </TealBigButton>
                  </ButtonGroup>
                </FlexCenter>
              </PaddingBox>
              <LogoSlider className="logo-slider">
                <div className="logo-slide">
                  <img src={PartnersLogo} alt="파트너사 로고" />
                  <img src={PartnersLogo} alt="파트너사 로고" />
                </div>
              </LogoSlider>
            </Header>
            <Box>
              <BoxInner>
                <Typography
                  as="div"
                  color={palette.teal200}
                  variant="medium20"
                  fontWeight={600}
                  lineHeight={1.5}
                >
                  마이프차 지도에서만 제공하는 특별한 기능!
                </Typography>
                <PaddingBox top={12} bottom={10}>
                  <SectionTitle as="div">AI 상권 분석 해설</SectionTitle>
                </PaddingBox>
                <Text>
                  데이터를 기반으로 AI가 상권 데이터를 해설해 드려요.
                  <br />
                  아래 튜토리얼을 따라가 보세요.
                </Text>
                <PaddingBox top={60}>
                  <ArcadeFrame>
                    <iframe
                      src="https://demo.arcade.software/qDAOvIik8fQfiAqrVwEv?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true"
                      title="AI 상권 분석 해설 튜토리얼"
                      loading="lazy"
                      allowFullScreen
                      allow="clipboard-write"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        border: 'none',
                        width: '100%',
                        height: '100%',
                        colorScheme: 'light',
                      }}
                    />
                  </ArcadeFrame>
                </PaddingBox>
              </BoxInner>
            </Box>
            {ACCORDION_CONTENTS.map((i) => (
              <Fragment key={i.title}>
                <DividerWrapper>
                  <Divider />
                </DividerWrapper>
                <AccordionContents item={i} />
              </Fragment>
            ))}
            <GrayBox>
              <BoxInner>
                <PaddingBox bottom={60}>
                  <SectionTitle as="div">
                    이런 분들이라면 마이프차 지도를 경험해 보세요.
                  </SectionTitle>
                </PaddingBox>
                <RoundBox>
                  <Flexbox gap="20px">
                    <IconCheck />
                    <p>
                      상가 경매 투자를 위한 고도화된{' '}
                      <strong>상권 분석 서비스가 필요할 때</strong>
                    </p>
                  </Flexbox>
                  <Flexbox gap="20px">
                    <IconCheck />
                    <p>
                      관심 있는 지역이 정말{' '}
                      <strong>투자 가치가 있는 상권인지 궁금할 때</strong>
                    </p>
                  </Flexbox>
                  <Flexbox gap="20px">
                    <IconCheck />
                    <p>
                      다음 매장을 오픈할{' '}
                      <strong>지역을 신중하게 찾고 있을 때</strong>
                    </p>
                  </Flexbox>
                  <Flexbox gap="20px">
                    <IconCheck />
                    <p>
                      서비스마다 제공하는 상권 정보가 다 다른데{' '}
                      <strong>정리할 시간이 없을 때</strong>
                    </p>
                  </Flexbox>
                  <Flexbox gap="20px">
                    <IconCheck />
                    <p>
                      관심 업종이 이 지역에서{' '}
                      <strong>매출이 잘 나올지 궁금할 때</strong>
                    </p>
                  </Flexbox>
                </RoundBox>
              </BoxInner>
            </GrayBox>
            <FooterSection>
              <BoxInner>
                <PaddingBox top={28}>
                  <SectionTitle as="div">
                    지금 바로, 내 인생을 결정할
                    <br />
                    가장 중요한 비즈니스의
                    <br />
                    성공 확률을 높여보세요!
                  </SectionTitle>
                  <BigButton
                    size="regular"
                    variant="fill"
                    color="white"
                    icon={OutLinkIconGray}
                    iconSize={20}
                    gap="4px"
                    reverse
                    as={IconButton}
                    onClick={onGuideClick}
                  >
                    마이프차 지도 미리보기
                  </BigButton>
                </PaddingBox>
                <img
                  src={MainImg002}
                  alt="마이프차 지도"
                  className="footer-image"
                />
              </BoxInner>
            </FooterSection>
          </Content>
          <FlexFullGrow />
          <Footer />
        </Body>
    </HorizontalFullContainer>
  );
};

const AccordionContents = ({item}: {item: AccordionContent}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  if (!item) {
    return null;
  }

  const active = item.items[activeIndex >= 0 ? activeIndex : 0];

  return (
    <Box>
      <BoxInner>
        <PaddingBox bottom={10}>
          <SectionTitle as="div">{item.title}</SectionTitle>
        </PaddingBox>
        {!!item.desc && <Text>{item.desc}</Text>}
        <ImageDescWrapper>
          <img src={active?.image} alt={active.title} />
          <AccordionDescItemList>
            {item.items?.map((i, idx) => {
              const isActive = idx === activeIndex;

              return (
                <AccordionDescItem
                  key={i.title}
                  onClick={() => setActiveIndex(isActive ? -1 : idx)}
                >
                  <div>
                    <AccordionDescTitle>{i.title}</AccordionDescTitle>
                    {isActive &&
                      (typeof i.desc === 'string' ? (
                        <AccordionDescContent
                          dangerouslySetInnerHTML={{__html: i.desc}}
                        />
                      ) : (
                        <AccordionDescContent>{i.desc}</AccordionDescContent>
                      ))}
                  </div>
                  <PaddingBox top={4} bottom={4}>
                    <img
                      src={isActive ? AccMinusIcon : AccPlusIcon}
                      alt={isActive ? '닫기' : '열기'}
                    />
                  </PaddingBox>
                </AccordionDescItem>
              );
            })}
          </AccordionDescItemList>
        </ImageDescWrapper>
      </BoxInner>
    </Box>
  );
};

export default Intro;

const Body = styled(ScrollBody)`
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Content = styled.div``;

const Box = styled.div`
  padding: 80px 120px 100px;
  min-height: 680px;
`;

const GrayBox = styled(Box)`
  background-color: #f3f5f7;
`;

const DividerWrapper = styled.div`
  padding: 0 120px;
`;

const BoxInner = styled.div`
  max-width: 1200px;
  margin: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding-top: 100px;
  background-image: url(${MapBg});
  background-size: cover;
  background-position: center top;
`;

const infiniteLoop = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(0);
  }
`;

const LogoSlider = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  .logo-slide {
    display: flex;
  }
  img {
    animation: ${infiniteLoop} 36s linear infinite;
    width: 100%;
  }
`;

const Headline = styled.div`
  font-size: 45px;
  line-height: 1.5;
  font-weight: 600;
  color: ${palette.black};
  & > * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    margin: 0;
  }
`;

const Text = styled(Typography)`
  font-size: 18px;
  line-height: 1.5;
  letter-spacing: -0.48px;
`;

const SectionTitle = styled.h3`
  font-size: 32px;
  font-weight: 600;
  line-height: 1.5;
  color: ${palette.black};
  letter-spacing: -0.96px;
`;

const BigButton = styled.button`
  padding: 0 16px;
  font-size: 16px;
  line-height: 1.25;
  font-weight: 600;
  height: 50px;
  border-radius: 10px;
`;

const TealBigButton = styled(BigButton)`
  border-color: ${palette.teal200};
  color: ${palette.teal200};
  background-color: ${palette.white};
`;

const RoundBox = styled(ColumnFlexBox)`
  padding: 60px;
  gap: 40px;
  border-radius: 40px;
  background-color: #fff;
  p {
    margin: 0;
    font-size: 24px;
    color: ${palette.black};
  }
`;

const FooterSection = styled.div`
  background-color: ${palette.primary500};
  padding: 60px 120px;
  ${BoxInner} {
    display: flex;
    justify-content: space-between;
    gap: 40px;
  }
  ${SectionTitle} {
    color: #fff;
  }
  ${BigButton} {
    margin-top: 60px;
  }
  & .footer-image {
    flex-shrink: 0;
    height: auto;
    max-width: 604px;
  }
  @media screen and (max-width: 1240px) {
    ${BoxInner} {
      flex-direction: column;
      align-items: center;
      max-width: 100%;
      text-align: center;
      & > ${PaddingBox} {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }
    ${BigButton} {
      margin-top: 24px;
    }
  }
`;

const ArcadeFrame = styled.div`
  position: relative;
  padding-bottom: calc(716 / 1074 * 100%);
  height: 0;
  width: 100%;
  border-radius: 10px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.25);
`;

const AccordionDescItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex-grow: 1;
`;

const AccordionDescItem = styled.div`
  cursor: pointer;
  width: 100%;
  padding-bottom: 24px;
  display: flex;
  gap: 24px;
  justify-content: space-between;
  align-items: flex-start;
  &:not(:last-of-type) {
    border-bottom: solid 1px ${palette.gray800};
  }
`;

const ImageDescWrapper = styled.div`
  display: flex;
  gap: 100px;
  margin-top: 60px;
  align-items: flex-start;
  & > img {
    max-width: 612px;
  }
  ${AccordionDescItemList} {
    max-width: 384px;
  }
  @media screen and (max-width: 1240px) {
    flex-direction: column;
    align-items: center;
    ${AccordionDescItemList} {
      max-width: 90%;
      width: 90%;
    }
  }
`;

const AccordionDescTitle = styled.div`
  color: ${palette.secondary900};
  font-size: 24px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: -0.72px;
`;

const AccordionDescContent = styled.div`
  color: ${palette.gray600};
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.72px;
  padding-top: 24px;
`;
